<script lang="ts">
  //experimental modified version from block-webgl-canvas
  //needed to use a shared canvas in the base-layout to avoid canvas reloads on page transitions

  import { getContext } from 'svelte';
  import { Model, AnimationLoop } from '@luma.gl/engine';
  import { Buffer, clear } from '@luma.gl/webgl';
  import { setParameters } from '@luma.gl/gltools';
  import { CanvasContextKEY, type WebGLRenderingContextState } from './SharedCanvasContext.svelte';

  export let fragmentShader: string; //required
  export let blend = false;
  export let animated = true;
  export let uniforms: any = {};
  export let updateUniforms = (_: any) => {
    //do nothing
  };
  export let modules: string[] = [];

  const Defaults = {
    vertexShader: `precision mediump float;
					attribute vec2 position;
					varying vec2 st;
					varying vec2 uv;

					void main() {
						uv = position;
						st = vec2(.5 + position.x * .5, .5 - position.y * .5);
						gl_Position = vec4(position, 0.0, 1.0);
					}`,
    verticies: new Float32Array([-1, -1, -1, 3, 3, -1]),
    vertexCount: 3,
    depth: {
      enable: false
    }
  };

  export let vertexShader = Defaults.vertexShader; //optional override
  export let verticies: Float32Array = Defaults.verticies; // optional override
  export let vertexCount = Defaults.vertexCount; // optional override

  let model: Model | undefined;
  let loop: AnimationLoop | undefined;

  const { gl } = getContext<WebGLRenderingContextState>(CanvasContextKEY);

  async function initializeContext(): Promise<WebGLRenderingContext> {
    const delay = (durationMs: number) => new Promise((resolve) => setTimeout(resolve, durationMs));

    while (!$gl) {
      await delay(100);
    }

    return $gl;
  }

  export const init = async () => {
    const gl = await initializeContext();

    if (loop) {
      return;
    }

    loop = new AnimationLoop({
      gl,
      onInitialize(animationProps) {
        const { gl } = animationProps;
        model = new Model(gl, {
          fs: fragmentShader,
          vs: vertexShader,
          attributes: {
            position: new Buffer(gl, verticies)
          },
          uniforms,
          modules,
          vertexCount
        });

        if (blend) {
          setParameters(gl, {
            blend: true,
            blendFunc: [gl.SRC_ALPHA_SATURATE, gl.ONE_MINUS_SRC_ALPHA]
          });
        }
        return animationProps;
      },

      onRender({ gl, time }) {
        clear(gl, { color: [0, 0, 0, 0] });
        uniforms.width = (gl.canvas as HTMLCanvasElement).clientWidth;
        uniforms.height = (gl.canvas as HTMLCanvasElement).clientHeight;
        if (animated) {
          uniforms.time = time / 1000;
          updateUniforms({ time });
        }
        model?.draw({ uniforms });
      },

      onFinalize(props) {
        if (!props?.gl) {
          return;
        }

        model?.delete();
        clear(gl, { color: [0, 0, 0, 0] });
        uniforms = null;
      }
    });
    loop.start();
  };

  export const cleanup = async () => {
    const gl = await initializeContext();

    loop?.stop();
    model?.delete();
    loop?.delete();
    clear(gl, { color: [0, 0, 0, 0] });
    loop = undefined;
    model = undefined;
    uniforms = {};
  };
</script>
